// ═══════════════════════════════════════════════════════════════
// BLACKBOARD — Reports
// Export training data (CSV, PDF), generate certificates on the fly
// ═══════════════════════════════════════════════════════════════

import { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Paper,
  Text,
  Group,
  SimpleGrid,
  Button,
  Select,
  Table,
  Badge,
  Loader,
  Center,
  Divider,
  Modal,
  TextInput,
  Stack,
  ScrollArea,
  Tabs,
} from '@mantine/core';
import { DateInput } from '@mantine/dates';
import { notifications } from '@mantine/notifications';
import {
  IconDownload,
  IconFileTypeCsv,
  IconFileTypePdf,
  IconCertificate,
  IconFilter,
  IconReportAnalytics,
  IconUsers,
  IconBook,
  IconSearch,
} from '@tabler/icons-react';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import dayjs from 'dayjs';
import {
  useBlackboardStore,
  type TrainingRecord,
  type BBUser,
  type Certificate,
} from '../../store/blackboardStore';
import { generateCertCode, generateCertificatePDF } from './utils/certificateUtils';

// ── Helpers ──────────────────────────────────────────────────

const loadLogoAsBase64 = async (): Promise<string | null> => {
  try {
    const response = await fetch('/logo.jpg');
    const blob = await response.blob();
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
};

function downloadBlob(content: string, filename: string, mimeType: string) {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function statusBadge(status: string) {
  const map: Record<string, { color: string; label: string }> = {
    completed: { color: 'green', label: 'Current' },
    expired: { color: 'red', label: 'Expired' },
    expiring: { color: 'orange', label: 'Expiring' },
  };
  const cfg = map[status] || { color: 'gray', label: status };
  return <Badge color={cfg.color} size="xs">{cfg.label}</Badge>;
}

// ── Types ────────────────────────────────────────────────────

interface FilteredRecord extends TrainingRecord {
  displayStatus: string;
  daysUntilExpiry: number | null;
  userDisplayName: string;
}

// ── Main Component ───────────────────────────────────────────

export default function Reports() {
  const {
    users,
    trainings,
    trainingRecords,
    certificates,
    loadUsers,
    loadTrainings,
    loadTrainingRecords,
    loadCertificates,
    createCertificate,
  } = useBlackboardStore();

  const [initialLoad, setInitialLoad] = useState(true);

  // Filters
  const [filterUser, setFilterUser] = useState<string | null>(null);
  const [filterTraining, setFilterTraining] = useState<string | null>(null);
  const [filterCategory, setFilterCategory] = useState<string | null>(null);
  const [filterStatus, setFilterStatus] = useState<string | null>(null);
  const [filterDateFrom, setFilterDateFrom] = useState<string | null>(null);
  const [filterDateTo, setFilterDateTo] = useState<string | null>(null);
  const [searchText, setSearchText] = useState('');

  // Certificate generation modal
  const [certModalOpen, setCertModalOpen] = useState(false);
  const [certUser, setCertUser] = useState<string | null>(null);
  const [certTraining, setCertTraining] = useState<string | null>(null);
  const [certDate, setCertDate] = useState<string | null>(dayjs().format('YYYY-MM-DD'));
  const [certGenerating, setCertGenerating] = useState(false);

  // Certificate filters
  const [certSearch, setCertSearch] = useState('');
  const [certFilterUser, setCertFilterUser] = useState<string | null>(null);
  const [certFilterTraining, setCertFilterTraining] = useState<string | null>(null);

  // Active tab
  const [activeTab, setActiveTab] = useState<string | null>('records');

  useEffect(() => {
    async function load() {
      await Promise.all([loadUsers(), loadTrainings(), loadTrainingRecords(), loadCertificates()]);
      setInitialLoad(false);
    }
    load();
  }, [loadUsers, loadTrainings, loadTrainingRecords, loadCertificates]);

  const now = dayjs();

  // Build user map
  const userMap = useMemo(() => {
    const m: Record<string, BBUser> = {};
    users.forEach(u => { m[String(u.id)] = u; });
    return m;
  }, [users]);

  // Dropdown options
  const userOptions = useMemo(() =>
    users.map(u => ({
      value: String(u.id),
      label: `${u.firstName || ''} ${u.lastName || ''}`.trim() || u.email,
    }))
  , [users]);

  const trainingOptions = useMemo(() =>
    trainings.map(t => ({ value: String(t.id), label: t.name }))
  , [trainings]);

  const categoryOptions = useMemo(() => {
    const cats = new Set<string>();
    trainings.forEach(t => { if (t.category) cats.add(t.category); });
    return Array.from(cats).sort().map(c => ({ value: c, label: c }));
  }, [trainings]);

  // Apply filters to records
  const filteredRecords = useMemo((): FilteredRecord[] => {
    return trainingRecords
      .map(r => {
        const u = userMap[r.userId];
        const displayName = r.userName || (u ? `${u.firstName || ''} ${u.lastName || ''}`.trim() : '') || 'Unknown';
        let displayStatus = 'completed';
        let daysUntilExpiry: number | null = null;

        if (r.expiresDate) {
          const exp = dayjs(r.expiresDate);
          daysUntilExpiry = exp.diff(now, 'day');
          if (daysUntilExpiry < 0) displayStatus = 'expired';
          else if (daysUntilExpiry <= 30) displayStatus = 'expiring';
        }

        return {
          ...r,
          displayStatus,
          daysUntilExpiry,
          userDisplayName: displayName,
        };
      })
      .filter(r => {
        if (filterUser && r.userId !== filterUser) return false;
        if (filterTraining && r.trainingId !== filterTraining) return false;
        if (filterStatus) {
          if (filterStatus === 'current' && r.displayStatus !== 'completed') return false;
          if (filterStatus === 'expired' && r.displayStatus !== 'expired') return false;
          if (filterStatus === 'expiring' && r.displayStatus !== 'expiring') return false;
        }
        if (filterCategory) {
          const training = trainings.find(t => String(t.id) === r.trainingId);
          if (!training || training.category !== filterCategory) return false;
        }
        if (filterDateFrom && r.completedDate && dayjs(r.completedDate).isBefore(dayjs(filterDateFrom))) return false;
        if (filterDateTo && r.completedDate && dayjs(r.completedDate).isAfter(dayjs(filterDateTo))) return false;
        if (searchText) {
          const q = searchText.toLowerCase();
          if (
            !r.userDisplayName.toLowerCase().includes(q) &&
            !(r.trainingName || '').toLowerCase().includes(q)
          ) return false;
        }
        return true;
      })
      .sort((a, b) => {
        // Expired first, then expiring, then by date
        const statusOrder = { expired: 0, expiring: 1, completed: 2 };
        const sa = statusOrder[a.displayStatus as keyof typeof statusOrder] ?? 2;
        const sb = statusOrder[b.displayStatus as keyof typeof statusOrder] ?? 2;
        if (sa !== sb) return sa - sb;
        return (b.completedDate || '').localeCompare(a.completedDate || '');
      });
  }, [trainingRecords, userMap, trainings, filterUser, filterTraining, filterCategory, filterStatus, filterDateFrom, filterDateTo, searchText, now]);

  // ── Export: CSV ────────────────────────────────────────────

  const exportCSV = useCallback(() => {
    const header = ['Name', 'Training', 'Category', 'Completed', 'Expires', 'Status', 'Certificate Code'];
    const rows = [header.join(',')];
    filteredRecords.forEach(r => {
      const training = trainings.find(t => String(t.id) === r.trainingId);
      rows.push([
        `"${r.userDisplayName}"`,
        `"${r.trainingName || '--'}"`,
        `"${training?.category || '--'}"`,
        r.completedDate || '--',
        r.expiresDate || 'N/A',
        r.displayStatus,
        r.certificateId || '--',
      ].join(','));
    });
    downloadBlob(rows.join('\n'), `training_records_${dayjs().format('YYYY-MM-DD')}.csv`, 'text/csv;charset=utf-8;');
    notifications.show({ title: 'Exported', message: `${filteredRecords.length} records exported to CSV.`, color: 'green' });
  }, [filteredRecords, trainings]);

  // ── Export: PDF ────────────────────────────────────────────

  const exportPDF = useCallback(async () => {
    const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'letter' });

    // Title
    doc.setFontSize(16);
    doc.setTextColor(25, 0, 155);
    doc.text('Training Records Report', 14, 20);
    doc.setFontSize(9);
    doc.setTextColor(100, 100, 100);
    doc.text(`Generated: ${dayjs().format('MMMM D, YYYY h:mm A')}`, 14, 27);

    // Filters summary
    const activeFilters: string[] = [];
    if (filterUser) {
      const u = userMap[filterUser];
      activeFilters.push(`Person: ${u ? `${u.firstName} ${u.lastName}`.trim() : filterUser}`);
    }
    if (filterTraining) {
      const t = trainings.find(tr => String(tr.id) === filterTraining);
      activeFilters.push(`Training: ${t?.name || filterTraining}`);
    }
    if (filterCategory) activeFilters.push(`Category: ${filterCategory}`);
    if (filterStatus) activeFilters.push(`Status: ${filterStatus}`);
    if (activeFilters.length > 0) {
      doc.text(`Filters: ${activeFilters.join(' | ')}`, 14, 33);
    }

    // Table
    const tableData = filteredRecords.map(r => {
      const training = trainings.find(t => String(t.id) === r.trainingId);
      return [
        r.userDisplayName,
        r.trainingName || '--',
        training?.category || '--',
        r.completedDate ? dayjs(r.completedDate).format('MMM D, YYYY') : '--',
        r.expiresDate ? dayjs(r.expiresDate).format('MMM D, YYYY') : 'N/A',
        r.displayStatus.charAt(0).toUpperCase() + r.displayStatus.slice(1),
      ];
    });

    autoTable(doc, {
      startY: activeFilters.length > 0 ? 38 : 32,
      head: [['Name', 'Training', 'Category', 'Completed', 'Expires', 'Status']],
      body: tableData,
      styles: { fontSize: 8, cellPadding: 2 },
      headStyles: { fillColor: [25, 0, 155], textColor: 255, fontStyle: 'bold' },
      alternateRowStyles: { fillColor: [245, 247, 250] },
      columnStyles: {
        5: {
          cellWidth: 22,
          fontStyle: 'bold',
        },
      },
      didParseCell: (data) => {
        if (data.section === 'body' && data.column.index === 5) {
          const val = String(data.cell.raw).toLowerCase();
          if (val === 'expired') data.cell.styles.textColor = [220, 38, 38];
          else if (val === 'expiring') data.cell.styles.textColor = [234, 88, 12];
          else data.cell.styles.textColor = [22, 163, 74];
        }
      },
    });

    // Footer
    const pageCount = doc.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(7);
      doc.setTextColor(150, 150, 150);
      doc.text(
        `OpenGov EMC — Blackboard Training Report | Page ${i} of ${pageCount}`,
        14,
        doc.internal.pageSize.height - 10,
      );
    }

    doc.save(`training_report_${dayjs().format('YYYY-MM-DD')}.pdf`);
    notifications.show({ title: 'Exported', message: `PDF report generated with ${filteredRecords.length} records.`, color: 'green' });
  }, [filteredRecords, trainings, userMap, filterUser, filterTraining, filterCategory, filterStatus]);

  // ── Export: Personnel Summary PDF ─────────────────────────

  const exportPersonnelPDF = useCallback(async () => {
    const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'letter' });

    doc.setFontSize(16);
    doc.setTextColor(25, 0, 155);
    doc.text('Personnel Training Summary', 14, 20);
    doc.setFontSize(9);
    doc.setTextColor(100, 100, 100);
    doc.text(`Generated: ${dayjs().format('MMMM D, YYYY h:mm A')}`, 14, 27);

    const tableData = users.map(u => {
      const name = `${u.firstName || ''} ${u.lastName || ''}`.trim() || u.email;
      const uRecords = trainingRecords.filter(r => r.userId === String(u.id) || r.userId === u.email || r.userName === name);
      const total = uRecords.length;
      const current = uRecords.filter(r => {
        if (!r.completedDate) return false;
        if (!r.expiresDate) return true;
        return dayjs(r.expiresDate).isAfter(now);
      }).length;
      const expired = uRecords.filter(r => r.expiresDate && dayjs(r.expiresDate).isBefore(now)).length;
      const pct = total > 0 ? Math.round((current / total) * 100) : 100;
      return [name, u.role || 'student', String(total), String(current), String(expired), `${pct}%`];
    });

    autoTable(doc, {
      startY: 32,
      head: [['Name', 'Role', 'Total', 'Current', 'Expired', 'Compliance']],
      body: tableData,
      styles: { fontSize: 8, cellPadding: 2 },
      headStyles: { fillColor: [25, 0, 155], textColor: 255, fontStyle: 'bold' },
      alternateRowStyles: { fillColor: [245, 247, 250] },
    });

    doc.save(`personnel_summary_${dayjs().format('YYYY-MM-DD')}.pdf`);
    notifications.show({ title: 'Exported', message: 'Personnel summary PDF generated.', color: 'green' });
  }, [users, trainingRecords, now]);

  // ── Certificate Generation (on the fly) ───────────────────

  const handleGenerateCertificate = async () => {
    if (!certUser || !certTraining) {
      notifications.show({ title: 'Required', message: 'Select a person and training.', color: 'yellow' });
      return;
    }

    setCertGenerating(true);
    try {
      const selectedUser = userMap[certUser];
      const selectedTraining = trainings.find(t => String(t.id) === certTraining);
      if (!selectedUser || !selectedTraining) throw new Error('Invalid selection');

      const studentName = `${selectedUser.firstName || ''} ${selectedUser.lastName || ''}`.trim() || selectedUser.email;
      const completedDateStr = certDate || dayjs().format('YYYY-MM-DD');
      let expiresDateStr = '';
      if (selectedTraining.frequencyDays > 0) {
        expiresDateStr = dayjs(completedDateStr).add(selectedTraining.frequencyDays, 'day').format('YYYY-MM-DD');
      }

      const certCode = generateCertCode();

      // Save certificate to Ignatius
      await createCertificate({
        userId: String(selectedUser.id),
        userName: studentName,
        trainingId: String(selectedTraining.id),
        trainingName: selectedTraining.name,
        certCode,
        completedDate: completedDateStr,
        expiresDate: expiresDateStr,
      });

      // Generate PDF
      const logoBase64 = await loadLogoAsBase64();
      const pdfDoc = await generateCertificatePDF({
        studentName,
        trainingName: selectedTraining.name,
        completionDate: dayjs(completedDateStr).format('MMMM D, YYYY'),
        certCode,
        orgName: 'Warm Springs Emergency Management Agency',
        logoBase64: logoBase64 ?? undefined,
      });

      const safeName = studentName.replace(/[^a-zA-Z0-9]/g, '_');
      pdfDoc.save(`Certificate_${safeName}_${certCode}.pdf`);

      notifications.show({
        title: 'Certificate Generated',
        message: `Certificate created for ${studentName} — ${selectedTraining.name}. Code: ${certCode}`,
        color: 'green',
      });

      setCertModalOpen(false);
      setCertUser(null);
      setCertTraining(null);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Unknown error';
      notifications.show({ title: 'Error', message: 'Failed to generate certificate: ' + msg, color: 'red' });
    } finally {
      setCertGenerating(false);
    }
  };

  // ── Download existing certificate ─────────────────────────

  const handleDownloadExistingCert = async (cert: Certificate) => {
    try {
      const logoBase64 = await loadLogoAsBase64();
      const pdfDoc = await generateCertificatePDF({
        studentName: cert.userName,
        trainingName: cert.trainingName,
        completionDate: cert.completedDate ? dayjs(cert.completedDate).format('MMMM D, YYYY') : '--',
        certCode: cert.certCode,
        orgName: 'Warm Springs Emergency Management Agency',
        logoBase64: logoBase64 ?? undefined,
      });
      const safeName = (cert.userName || 'student').replace(/[^a-zA-Z0-9]/g, '_');
      pdfDoc.save(`Certificate_${safeName}_${cert.certCode}.pdf`);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Unknown error';
      notifications.show({ title: 'Error', message: 'Failed to download: ' + msg, color: 'red' });
    }
  };

  // ── Clear filters ─────────────────────────────────────────

  const clearFilters = () => {
    setFilterUser(null);
    setFilterTraining(null);
    setFilterCategory(null);
    setFilterStatus(null);
    setFilterDateFrom(null);
    setFilterDateTo(null);
    setSearchText('');
  };

  const hasFilters = filterUser || filterTraining || filterCategory || filterStatus || filterDateFrom || filterDateTo || searchText;

  // Filtered certificates
  const filteredCertificates = useMemo(() => {
    return certificates.filter(cert => {
      if (certFilterUser && cert.userId !== certFilterUser) return false;
      if (certFilterTraining && cert.trainingId !== certFilterTraining) return false;
      if (certSearch) {
        const q = certSearch.toLowerCase();
        if (
          !cert.userName.toLowerCase().includes(q) &&
          !cert.trainingName.toLowerCase().includes(q) &&
          !cert.certCode.toLowerCase().includes(q)
        ) return false;
      }
      return true;
    });
  }, [certificates, certFilterUser, certFilterTraining, certSearch]);

  const hasCertFilters = certSearch || certFilterUser || certFilterTraining;

  // ── Render ─────────────────────────────────────────────────

  if (initialLoad) {
    return (
      <Center h={400}>
        <Loader color="blue" />
      </Center>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Group justify="space-between" align="center" mb="lg">
        <Box>
          <Text size="10px" fw={700} c="dimmed" style={{ textTransform: 'uppercase', letterSpacing: 2 }}>
            Training Management
          </Text>
          <Group gap="sm" align="center">
            <IconReportAnalytics size={18} color="#1a365d" />
            <Text size="xl" fw={700} c="#1a365d">
              Reports
            </Text>
          </Group>
        </Box>
        <Button
          color="indigo"
          leftSection={<IconCertificate size={14} />}
          onClick={() => setCertModalOpen(true)}
        >
          Generate Certificate
        </Button>
      </Group>

      <Tabs value={activeTab} onChange={setActiveTab}>
        <Tabs.List mb="md">
          <Tabs.Tab value="records" leftSection={<IconBook size={14} />}>
            Training Records
          </Tabs.Tab>
          <Tabs.Tab value="certificates" leftSection={<IconCertificate size={14} />}>
            Certificates ({certificates.length})
          </Tabs.Tab>
        </Tabs.List>

        {/* ── Training Records Tab ─────────────────────────────── */}
        <Tabs.Panel value="records">
          {/* Filters */}
          <Paper p="md" withBorder mb="md">
            <Group justify="space-between" align="center" mb="sm">
              <Group gap="xs">
                <IconFilter size={14} color="#627d98" />
                <Text size="sm" fw={600}>Filters</Text>
              </Group>
              {hasFilters && (
                <Button variant="subtle" size="xs" color="gray" onClick={clearFilters}>
                  Clear All
                </Button>
              )}
            </Group>
            <SimpleGrid cols={{ base: 1, sm: 2, md: 3, lg: 6 }} spacing="sm">
              <Select
                placeholder="Person"
                data={userOptions}
                value={filterUser}
                onChange={setFilterUser}
                clearable
                searchable
                size="xs"
                leftSection={<IconUsers size={12} />}
              />
              <Select
                placeholder="Training"
                data={trainingOptions}
                value={filterTraining}
                onChange={setFilterTraining}
                clearable
                searchable
                size="xs"
                leftSection={<IconBook size={12} />}
              />
              <Select
                placeholder="Category"
                data={categoryOptions}
                value={filterCategory}
                onChange={setFilterCategory}
                clearable
                size="xs"
              />
              <Select
                placeholder="Status"
                data={[
                  { value: 'current', label: 'Current' },
                  { value: 'expired', label: 'Expired' },
                  { value: 'expiring', label: 'Expiring (30d)' },
                ]}
                value={filterStatus}
                onChange={setFilterStatus}
                clearable
                size="xs"
              />
              <DateInput
                placeholder="From date"
                value={filterDateFrom}
                onChange={setFilterDateFrom}
                size="xs"
                clearable
              />
              <DateInput
                placeholder="To date"
                value={filterDateTo}
                onChange={setFilterDateTo}
                size="xs"
                clearable
              />
            </SimpleGrid>
            <TextInput
              placeholder="Search by name or training..."
              value={searchText}
              onChange={e => setSearchText(e.currentTarget.value)}
              leftSection={<IconSearch size={12} />}
              size="xs"
              mt="sm"
              style={{ maxWidth: 400 }}
            />
          </Paper>

          {/* Export buttons */}
          <Group gap="sm" mb="md">
            <Button
              size="xs"
              variant="light"
              color="green"
              leftSection={<IconFileTypeCsv size={14} />}
              onClick={exportCSV}
            >
              Export CSV ({filteredRecords.length})
            </Button>
            <Button
              size="xs"
              variant="light"
              color="red"
              leftSection={<IconFileTypePdf size={14} />}
              onClick={exportPDF}
            >
              Export PDF ({filteredRecords.length})
            </Button>
            <Button
              size="xs"
              variant="light"
              color="blue"
              leftSection={<IconUsers size={14} />}
              onClick={exportPersonnelPDF}
            >
              Personnel Summary PDF
            </Button>
          </Group>

          {/* Records Table */}
          <Paper p="md" withBorder>
            <Text size="xs" c="dimmed" mb="sm">
              Showing {filteredRecords.length} of {trainingRecords.length} records
            </Text>
            {filteredRecords.length === 0 ? (
              <Center py="xl">
                <Text c="dimmed">No records match your filters.</Text>
              </Center>
            ) : (
              <ScrollArea h={500}>
                <Table stickyHeader>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Name</Table.Th>
                      <Table.Th>Training</Table.Th>
                      <Table.Th>Completed</Table.Th>
                      <Table.Th>Expires</Table.Th>
                      <Table.Th>Status</Table.Th>
                      <Table.Th>Cert Code</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {filteredRecords.slice(0, 200).map(r => (
                      <Table.Tr key={r.id}>
                        <Table.Td><Text size="sm" fw={500}>{r.userDisplayName}</Text></Table.Td>
                        <Table.Td><Text size="sm">{r.trainingName || '--'}</Text></Table.Td>
                        <Table.Td>
                          <Text size="xs">{r.completedDate ? dayjs(r.completedDate).format('MMM D, YYYY') : '--'}</Text>
                        </Table.Td>
                        <Table.Td>
                          <Text size="xs">{r.expiresDate ? dayjs(r.expiresDate).format('MMM D, YYYY') : 'N/A'}</Text>
                        </Table.Td>
                        <Table.Td>{statusBadge(r.displayStatus)}</Table.Td>
                        <Table.Td>
                          <Text size="xs" c="dimmed" ff="monospace">{r.certificateId || '--'}</Text>
                        </Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
                {filteredRecords.length > 200 && (
                  <Text size="xs" c="dimmed" ta="center" mt="sm">
                    Showing first 200 of {filteredRecords.length} records. Export to see all.
                  </Text>
                )}
              </ScrollArea>
            )}
          </Paper>
        </Tabs.Panel>

        {/* ── Certificates Tab ─────────────────────────────────── */}
        <Tabs.Panel value="certificates">
          {/* Search & Filter */}
          <Paper p="md" withBorder mb="md">
            <Group justify="space-between" align="center" mb="sm">
              <Group gap="xs">
                <IconFilter size={14} color="#627d98" />
                <Text size="sm" fw={600}>Search & Filter</Text>
              </Group>
              {hasCertFilters && (
                <Button variant="subtle" size="xs" color="gray" onClick={() => {
                  setCertSearch('');
                  setCertFilterUser(null);
                  setCertFilterTraining(null);
                }}>
                  Clear All
                </Button>
              )}
            </Group>
            <Group gap="sm">
              <TextInput
                placeholder="Search by name, training, or code..."
                value={certSearch}
                onChange={e => setCertSearch(e.currentTarget.value)}
                leftSection={<IconSearch size={12} />}
                size="xs"
                style={{ flex: 1, minWidth: 200 }}
              />
              <Select
                placeholder="Filter by person"
                data={userOptions}
                value={certFilterUser}
                onChange={setCertFilterUser}
                clearable
                searchable
                size="xs"
                style={{ minWidth: 180 }}
                leftSection={<IconUsers size={12} />}
              />
              <Select
                placeholder="Filter by training"
                data={trainingOptions}
                value={certFilterTraining}
                onChange={setCertFilterTraining}
                clearable
                searchable
                size="xs"
                style={{ minWidth: 180 }}
                leftSection={<IconBook size={12} />}
              />
            </Group>
          </Paper>

          <Paper p="md" withBorder>
            <Text size="xs" c="dimmed" mb="sm">
              Showing {filteredCertificates.length} of {certificates.length} certificates
            </Text>
            {certificates.length === 0 ? (
              <Center py="xl">
                <Stack align="center" gap="sm">
                  <IconCertificate size={32} color="#868e96" />
                  <Text c="dimmed">No certificates generated yet.</Text>
                  <Button
                    variant="light"
                    color="indigo"
                    size="sm"
                    onClick={() => setCertModalOpen(true)}
                  >
                    Generate First Certificate
                  </Button>
                </Stack>
              </Center>
            ) : filteredCertificates.length === 0 ? (
              <Center py="xl">
                <Text c="dimmed">No certificates match your search.</Text>
              </Center>
            ) : (
              <ScrollArea h={500}>
                <Table stickyHeader>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Name</Table.Th>
                      <Table.Th>Training</Table.Th>
                      <Table.Th>Completed</Table.Th>
                      <Table.Th>Expires</Table.Th>
                      <Table.Th>Code</Table.Th>
                      <Table.Th>Actions</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {filteredCertificates.map(cert => (
                      <Table.Tr key={cert.id}>
                        <Table.Td><Text size="sm" fw={500}>{cert.userName}</Text></Table.Td>
                        <Table.Td><Text size="sm">{cert.trainingName}</Text></Table.Td>
                        <Table.Td>
                          <Text size="xs">{cert.completedDate ? dayjs(cert.completedDate).format('MMM D, YYYY') : '--'}</Text>
                        </Table.Td>
                        <Table.Td>
                          <Text size="xs">{cert.expiresDate ? dayjs(cert.expiresDate).format('MMM D, YYYY') : 'N/A'}</Text>
                        </Table.Td>
                        <Table.Td>
                          <Text size="xs" ff="monospace" c="blue">{cert.certCode}</Text>
                        </Table.Td>
                        <Table.Td>
                          <Button
                            size="xs"
                            variant="subtle"
                            color="blue"
                            leftSection={<IconDownload size={10} />}
                            onClick={() => handleDownloadExistingCert(cert)}
                          >
                            Download PDF
                          </Button>
                        </Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              </ScrollArea>
            )}
          </Paper>
        </Tabs.Panel>
      </Tabs>

      {/* ── Generate Certificate Modal ─────────────────────────── */}
      <Modal
        opened={certModalOpen}
        onClose={() => setCertModalOpen(false)}
        title={<Text fw={600}>Generate Training Certificate</Text>}
        size="md"
      >
        <Stack gap="md">
          <Text size="sm" c="dimmed">
            Create a certificate with QR code verification. The certificate will be saved
            to the system and a PDF will be downloaded.
          </Text>
          <Select
            label="Person"
            placeholder="Select person"
            data={userOptions}
            value={certUser}
            onChange={setCertUser}
            searchable
            required
            leftSection={<IconUsers size={14} />}
          />
          <Select
            label="Training"
            placeholder="Select training"
            data={trainingOptions}
            value={certTraining}
            onChange={setCertTraining}
            searchable
            required
            leftSection={<IconBook size={14} />}
          />
          <DateInput
            label="Completion Date"
            value={certDate}
            onChange={setCertDate}
            size="sm"
          />
          <Divider />
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setCertModalOpen(false)}>Cancel</Button>
            <Button
              color="indigo"
              leftSection={<IconCertificate size={14} />}
              onClick={handleGenerateCertificate}
              loading={certGenerating}
            >
              Generate & Download
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Box>
  );
}
